// i18n keys of the work package "release" — Bugify's "Deploy now to all systems" (owner 2026-11-03): the Confirm
// question, the deploy's progress card and list banner (src/components/Bugify/Release.tsx), and the "the system is being
// updated" notice every page of every installation shows while a deploy restarts it (src/components/DeployNotice.tsx).
// en / zh / th. ⛔ Fill all three: an empty th value falls back to English.
// Placeholders: {{time}} is always a formatted elapsed time (m:ss) or a clock time; {{min}}, {{n}}, {{i}} are numbers;
// {{systems}} and {{names}} are lists already joined for the reader's language. The bgr_why_* reasons take no
// placeholders, and their English is the server's WHY_EN (server/src/services/bugifyRelease.ts) word for word.
export const en: Record<string, string> = {
  bgr_modal_title: 'Confirm this fix',
  bgr_modal_lead: 'Confirming says the fix works on this test system. When should it go out to the other systems?',
  bgr_confirm_only: 'Confirm that this fix works?',
  bgr_options_unavailable: 'The deploy options could not be loaded. Confirming now sends the fix out automatically with the nightly release.',
  bgr_opt_tonight: 'Automatically after 20:00 tonight',
  bgr_opt_tonight_hint: 'Recommended — nobody is interrupted during working hours.',
  bgr_opt_tonight_night: 'Automatically in the next few minutes',
  bgr_opt_tonight_night_hint: 'The nightly release is running now; it updates one system at a time.',
  bgr_opt_now: 'Deploy now to all systems',
  bgr_opt_now_hint: '{{systems}} are updated one after another. Each one restarts and is unavailable for about a minute; people using it see an "updating" notice.',
  bgr_systems_unknown: 'The other systems',
  bgr_systems_unknown_mid: 'the other systems',
  bgr_offline_warn: 'Bugify’s release machine last checked in {{min}} min ago. The deploy starts when it is back; if that takes more than 30 minutes, the fix goes out with the nightly release instead.',
  bgr_busy_warn: 'Another deploy to these systems is running; this one starts right after it.',
  bgr_not_before: 'Starts at {{time}}: the systems were restarted recently, and immediate deploys are spaced 30 minutes apart.',
  bgr_failed_recent_warn: 'The last deploy stopped at {{system}} {{min}} min ago. Deploying now tries it again.',
  bgr_fresh_warn: 'The test system was updated {{min}} min ago. Everything in that version goes out with this fix.',
  bgr_riding: 'Unconfirmed fixes that go out with it: {{n}}',
  bgr_submitting: 'Saving…',
  bgr_err_unauthenticated: 'Your session has ended — please sign in again.',
  bgr_err_already: 'This item was already confirmed by {{who}}.',
  bgr_err_not_ai_done: 'This item is no longer waiting for confirmation.',
  bgr_err_unknown_release: 'Unknown deploy choice — please reload the page.',
  bgr_err_bad_mode: 'Unknown deploy request — please reload the page.',
  bgr_err_now_unavailable: 'Deploy now is not available on this system right now.',
  bgr_err_nothing_owed: 'There is nothing waiting to be deployed.',
  bgr_err_already_requested: 'An immediate deploy is already requested.',
  bgr_err_retry_used: 'This failed deploy has already been retried once. An admin can try again.',
  bgr_err_retry_pointless: 'The last deploy was refused by a system that runs a different code line. Trying again cannot help until that code is merged.',
  bgr_err_daily_cap: 'Today’s limit of immediate deploys has been reached. The fix goes out with the nightly release.',
  bgr_err_not_running: 'That deploy is no longer running.',
  bgr_err_forbidden: 'Only an admin or a Bugify approver can do this.',
  bgr_err_generic: 'Could not save — please try again.',
  bgr_waiting: 'Waiting for Bugify to start the deploy…',
  bgr_waiting_hint: 'It usually starts within a minute.',
  bgr_held: 'Waiting: {{reason}}. Bugify tries again shortly.',
  bgr_held_night: 'Paused: {{reason}}. Bugify tries again in a few minutes.',
  bgr_nightly: 'Nightly release',
  bgr_preparing: 'Preparing the deploy…',
  bgr_between: 'Moving on to the next system…',
  bgr_finishing: 'Finishing up…',
  bgr_running: 'Updating {{system}} — {{step}}',
  bgr_of: 'System {{i}} of {{n}}',
  bgr_elapsed: 'Elapsed {{time}}',
  bgr_requested_by: 'Requested by {{who}}',
  bgr_stalled: 'No word from the release machine for {{min}} min — it may still be working.',
  bgr_slow: 'This is taking much longer than usual — Bugify is still waiting for it.',
  bgr_done: 'Deployed to all systems in {{time}}.',
  bgr_done_partial: 'Updated: {{names}}. The rest follow in the next few minutes.',
  bgr_done_noop: 'All the other systems were already up to date.',
  bgr_failed: 'The deploy stopped at {{system}}: {{error}}',
  bgr_failed_early: 'The deploy stopped before any system was touched: {{error}}',
  bgr_failed_partial: '{{system}} may be half-updated — open it first.',
  bgr_failed_hint: 'The systems after it were not touched. Bugify does not retry on its own for 6 hours; press "Try again now" once the cause is fixed.',
  bgr_blocked: 'The deploy could not start: {{reason}}. Nothing was deployed.',
  bgr_blocked_hint: 'The fix goes out with the nightly release once this is resolved, or press "Try again now".',
  bgr_failed_hint_noretry: 'The systems after it were not touched. Bugify does not retry on its own for 6 hours.',
  bgr_blocked_hint_noretry: 'The fix goes out with the nightly release once this is resolved.',
  bgr_now_unavail_followers: 'Deploy now is not available: no other systems are configured on Bugify’s release machine.',
  bgr_now_unavail_offline: 'Deploy now is not available: Bugify’s release machine last checked in {{min}} min ago. The fix goes out with the nightly release once it is back.',
  bgr_now_unavail_never: 'Deploy now is not available: Bugify’s release machine has not checked in yet.',
  bgr_checked: 'Check only — the deploy path works and nothing was deployed.',
  bgr_stopped: 'Stopped by {{who}}. Updated before the stop: {{names}}. The rest go out with the nightly release.',
  bgr_none: 'none',
  bgr_expired: 'The deploy did not start within 30 minutes ({{reason}}). It goes out automatically after 20:00 tonight.',
  bgr_expired_night: 'The deploy did not start within 30 minutes ({{reason}}). It goes out with the nightly release in the next few minutes.',
  bgr_lost: 'Lost contact with the release machine during the deploy. The systems may still be finishing; it is retried after 20:00 tonight.',
  bgr_lost_night: 'Lost contact with the release machine during the deploy. The systems may still be finishing; the nightly release tries again in a few minutes.',
  bgr_deploy_now_btn: 'Deploy now',
  bgr_retry_btn: 'Try again now',
  bgr_now_confirm: 'Deploy now to {{systems}}? Each one restarts and is unavailable for about a minute.',
  bgr_stop_btn: 'Stop after this system',
  bgr_stop_confirm: 'Stop the deploy after {{system}}? The systems already updated stay updated; the rest go out with the nightly release.',
  bgr_stopping: 'Stopping after {{system}}…',
  bgr_fanout_now: 'Going out to the other systems now — see the progress below.',
  bgr_progress_label: 'Deploy progress',
  bgr_t_pending: 'Waiting',
  bgr_t_not_updated: 'Not updated',
  bgr_t_current: 'Already up to date',
  bgr_t_done: 'Updated',
  bgr_t_failed: 'Failed',
  bgr_t_held: 'On hold',
  bgr_step_start: 'starting',
  bgr_step_lock: 'waiting for the server',
  bgr_step_gates: 'safety checks',
  bgr_step_build: 'building the new version',
  bgr_step_tsc: 'checking the server code',
  bgr_step_upload: 'uploading',
  bgr_step_restart: 'restarting',
  bgr_step_verify: 'waiting for it to answer',
  bgr_step_health: 'checking it is back',
  bgr_banner_running: 'Deploying to the other systems — {{system}}: {{step}}',
  bgr_banner_waiting: 'Deploy now requested — waiting for Bugify to start',
  bgr_banner_done: 'Deploy finished: {{names}}',
  bgr_banner_failed: 'Deploy stopped at {{system}}',
  bgr_banner_failed_early: 'Deploy stopped before any system was touched',
  bgr_banner_blocked: 'Deploy now did not start: {{reason}}',
  bgr_banner_open: 'Details',
  // English = the server's WHY_EN, word for word.
  bgr_why_lock_busy: 'another release to these systems is already running',
  bgr_why_server_lock: 'another deploy is using that system\'s server right now',
  bgr_why_source_unknown: 'the test system\'s current version cannot be read',
  bgr_why_source_dirty: 'the test system runs uncommitted changes — commit and redeploy them first',
  bgr_why_source_down: 'the test system is not answering',
  bgr_why_source_moved: 'the test system was updated while the deploy was being prepared',
  bgr_why_soak: 'the test system was just updated with changes nobody has looked at yet, so it waits until that version has run for a while',
  bgr_why_commit_not_live: 'the test system is not running the confirmed fix yet',
  bgr_why_check_only: 'check only — nothing was deployed',
  bgr_why_no_followers: 'no other systems are configured',
  bgr_why_config: 'a system\'s configuration on the release machine is missing or incomplete',
  bgr_why_commit_missing: 'the release machine does not have the version the test system runs',
  bgr_why_worktree: 'the release machine could not prepare its copy of the code',
  bgr_why_deploy_failed: 'the deploy to that system failed',
  bgr_why_no_answer: 'the system did not answer after its restart',
  bgr_why_marker_stuck: 'the system did not record the new version',
  bgr_why_diverged: 'that system runs a different code line, so nothing was touched there',
  bgr_why_interrupted: 'the release was interrupted while a deploy was in progress',
  bgr_why_overtime: 'the release has been running for more than 3 hours',
  bgr_why_no_summary: 'the release script ended without a report',
  bgr_why_stopped: 'stopped on request',
  bgr_why_not_in_tree: 'the release script is not on the release machine yet',
  bgr_why_unaccounted: 'the release reported success without naming any system',
  bgr_why_not_picked: 'the release machine did not pick it up',
  bgr_why_recent_failure: 'the nightly release of this version failed a short while ago, so it is not retried until a person has looked',
  sysup_banner: 'The system is being updated. It may be unavailable for about a minute shortly — please save your work.',
  sysup_reconnecting: 'Reconnecting to the server…',
  sysup_title: 'Updating the system',
  sysup_body: 'This usually takes about a minute. The page reconnects automatically — no need to refresh.',
  sysup_lost_title: 'Connection lost',
  sysup_lost_body: 'Trying to reconnect automatically…',
  sysup_elapsed: 'Elapsed {{time}}',
  sysup_slow: 'Taking longer than usual — still trying.',
  sysup_retry: 'Try now',
  sysup_reload: 'Reload',
  sysup_continue: 'Continue anyway',
  sysup_back: 'Back online',
  sysup_finishing: 'The system is back — finishing the update…',
  sysup_dismiss: 'Dismiss',
  sysup_progress_label: 'Update progress',
  sysup_try_again: 'The system is being updated or cannot be reached — please try again in a minute.',
};

export const zh: Record<string, string> = {
  bgr_modal_title: '确认这个修复',
  bgr_modal_lead: '确认表示这个修复在测试系统上已经没问题。什么时候更新到其他系统？',
  bgr_confirm_only: '确认这个修复没问题？',
  bgr_options_unavailable: '无法载入部署选项。现在确认的话，修复会随夜间发布自动部署。',
  bgr_opt_tonight: '今晚 20:00 之后自动部署',
  bgr_opt_tonight_hint: '推荐——不影响白天上班的人。',
  bgr_opt_tonight_night: '接下来几分钟内自动部署',
  bgr_opt_tonight_night_hint: '夜间发布正在进行，每次更新一个系统。',
  bgr_opt_now: '立即部署到所有系统',
  bgr_opt_now_hint: '{{systems}} 会逐个更新。每个系统会重启，约一分钟无法使用；正在使用的人会看到“正在更新”的提示。',
  bgr_systems_unknown: '其他系统',
  bgr_systems_unknown_mid: '其他系统',
  bgr_offline_warn: 'Bugify 的发布机器 {{min}} 分钟前最后一次上线。它恢复后就会开始部署；如果超过 30 分钟，修复改为随夜间发布部署。',
  bgr_busy_warn: '另一次部署正在进行，这次会在它完成后立刻开始。',
  bgr_not_before: '将在 {{time}} 开始：这些系统刚重启过，立即部署之间至少间隔 30 分钟。',
  bgr_failed_recent_warn: '上一次部署 {{min}} 分钟前在 {{system}} 停止。现在部署会再试一次。',
  bgr_fresh_warn: '测试系统 {{min}} 分钟前刚更新过，该版本里的所有改动都会和这个修复一起发出。',
  bgr_riding: '一同发出、尚未确认的修复：{{n}} 个',
  bgr_submitting: '正在保存…',
  bgr_err_unauthenticated: '登录已失效，请重新登录。',
  bgr_err_already: '这一项已经由 {{who}} 确认过了。',
  bgr_err_not_ai_done: '这一项已经不在等待确认了。',
  bgr_err_unknown_release: '未知的部署选项，请重新加载页面。',
  bgr_err_bad_mode: '未知的部署请求，请重新加载页面。',
  bgr_err_now_unavailable: '此系统目前无法立即部署。',
  bgr_err_nothing_owed: '没有等待部署的内容。',
  bgr_err_already_requested: '已经有人请求了立即部署。',
  bgr_err_retry_used: '这次失败的部署已经重试过一次。管理员可以再试。',
  bgr_err_retry_pointless: '上一次部署被一个运行另一条代码线的系统拒绝。在代码合并之前，重试也没有用。',
  bgr_err_daily_cap: '今天的立即部署次数已达上限，修复会随夜间发布部署。',
  bgr_err_not_running: '那次部署已经不在进行了。',
  bgr_err_forbidden: '只有管理员或 Bugify 审批人可以执行此操作。',
  bgr_err_generic: '保存失败，请重试。',
  bgr_waiting: '等待 Bugify 开始部署…',
  bgr_waiting_hint: '通常一分钟内开始。',
  bgr_held: '等待中：{{reason}}。Bugify 稍后会再试。',
  bgr_held_night: '已暂停：{{reason}}。Bugify 几分钟后会再试。',
  bgr_nightly: '夜间发布',
  bgr_preparing: '正在准备部署…',
  bgr_between: '正在转到下一个系统…',
  bgr_finishing: '正在收尾…',
  bgr_running: '正在更新 {{system}} —— {{step}}',
  bgr_of: '第 {{i}} / {{n}} 个系统',
  bgr_elapsed: '已用时 {{time}}',
  bgr_requested_by: '由 {{who}} 发起',
  bgr_stalled: '发布机器已有 {{min}} 分钟没有回报——可能仍在进行。',
  bgr_slow: '比平时慢很多——Bugify 仍在等待。',
  bgr_done: '已部署到所有系统，用时 {{time}}。',
  bgr_done_partial: '已更新：{{names}}。其余系统将在接下来几分钟内更新。',
  bgr_done_noop: '其他系统都已经是最新版本。',
  bgr_failed: '部署在 {{system}} 停止：{{error}}',
  bgr_failed_early: '部署在改动任何系统之前就停止了：{{error}}',
  bgr_failed_partial: '{{system}} 可能只更新了一半——请先检查它。',
  bgr_failed_hint: '之后的系统都没有改动。Bugify 6 小时内不会自动重试；问题解决后请按“立即重试”。',
  bgr_blocked: '部署无法开始：{{reason}}。没有部署任何内容。',
  bgr_blocked_hint: '问题解决后，修复会随夜间发布部署；也可以按“立即重试”。',
  bgr_failed_hint_noretry: '之后的系统都没有改动。Bugify 6 小时内不会自动重试。',
  bgr_blocked_hint_noretry: '问题解决后，修复会随夜间发布部署。',
  bgr_now_unavail_followers: '无法立即部署：Bugify 的发布机器上没有配置其他系统。',
  bgr_now_unavail_offline: '无法立即部署：Bugify 的发布机器 {{min}} 分钟前最后一次上线。它恢复后，修复会随夜间发布部署。',
  bgr_now_unavail_never: '无法立即部署：Bugify 的发布机器还没有上线过。',
  bgr_checked: '仅检查——部署流程正常，没有部署任何内容。',
  bgr_stopped: '已由 {{who}} 停止。停止前已更新：{{names}}。其余系统随夜间发布更新。',
  bgr_none: '无',
  bgr_expired: '部署 30 分钟内没有开始（{{reason}}），将在今晚 20:00 之后自动部署。',
  bgr_expired_night: '部署 30 分钟内没有开始（{{reason}}），将在接下来几分钟内随夜间发布部署。',
  bgr_lost: '部署过程中与发布机器失去联系。系统可能仍在完成更新；今晚 20:00 之后会再试。',
  bgr_lost_night: '部署过程中与发布机器失去联系。系统可能仍在完成更新；夜间发布几分钟后会再试。',
  bgr_deploy_now_btn: '立即部署',
  bgr_retry_btn: '立即重试',
  bgr_now_confirm: '现在部署到 {{systems}}？每个系统会重启，约一分钟无法使用。',
  bgr_stop_btn: '当前系统完成后停止',
  bgr_stop_confirm: '在 {{system}} 完成后停止部署？已更新的系统保持不变，其余系统随夜间发布更新。',
  bgr_stopping: '将在 {{system}} 完成后停止…',
  bgr_fanout_now: '正在发往其他系统——进度见下方。',
  bgr_progress_label: '部署进度',
  bgr_t_pending: '等待中',
  bgr_t_not_updated: '未更新',
  bgr_t_current: '已是最新',
  bgr_t_done: '已更新',
  bgr_t_failed: '失败',
  bgr_t_held: '暂缓',
  bgr_step_start: '开始',
  bgr_step_lock: '等待服务器',
  bgr_step_gates: '安全检查',
  bgr_step_build: '构建新版本',
  bgr_step_tsc: '检查服务器代码',
  bgr_step_upload: '上传中',
  bgr_step_restart: '重启中',
  bgr_step_verify: '等待系统响应',
  bgr_step_health: '确认系统已恢复',
  bgr_banner_running: '正在部署到其他系统 —— {{system}}：{{step}}',
  bgr_banner_waiting: '已请求立即部署——等待 Bugify 开始',
  bgr_banner_done: '部署完成：{{names}}',
  bgr_banner_failed: '部署在 {{system}} 停止',
  bgr_banner_failed_early: '部署在改动任何系统之前停止',
  bgr_banner_blocked: '立即部署没有开始：{{reason}}',
  bgr_banner_open: '详情',
  bgr_why_lock_busy: '另一次发布正在进行',
  bgr_why_server_lock: '那个系统的服务器正被另一次部署占用',
  bgr_why_source_unknown: '读不到测试系统当前的版本',
  bgr_why_source_dirty: '测试系统运行的是未提交的改动——请先提交并重新部署',
  bgr_why_source_down: '测试系统没有响应',
  bgr_why_source_moved: '准备部署期间测试系统又更新了',
  bgr_why_soak: '测试系统刚更新，其中有还没人看过的改动，所以要等这个版本运行一段时间',
  bgr_why_commit_not_live: '测试系统还没有运行已确认的修复',
  bgr_why_check_only: '仅检查——没有部署任何内容',
  bgr_why_no_followers: '没有配置其他系统',
  bgr_why_config: '发布机器上某个系统的配置缺失或不完整',
  bgr_why_commit_missing: '发布机器上没有测试系统正在运行的版本',
  bgr_why_worktree: '发布机器无法准备代码副本',
  bgr_why_deploy_failed: '部署到该系统失败',
  bgr_why_no_answer: '系统重启后没有响应',
  bgr_why_marker_stuck: '系统没有记录新版本',
  bgr_why_diverged: '该系统运行的是另一条代码线，所以没有改动它',
  bgr_why_interrupted: '发布在部署进行中被中断',
  bgr_why_overtime: '发布已运行超过 3 小时',
  bgr_why_no_summary: '发布脚本结束时没有给出结果',
  bgr_why_stopped: '已按要求停止',
  bgr_why_not_in_tree: '发布机器上还没有发布脚本',
  bgr_why_unaccounted: '发布报告成功，但没有列出任何系统',
  bgr_why_not_picked: '发布机器没有接手',
  bgr_why_recent_failure: '这个版本的每晚发布刚失败过，在有人查看之前不会再试',
  sysup_banner: '系统正在更新，稍后约有一分钟无法使用，请先保存手头的内容。',
  sysup_reconnecting: '正在重新连接服务器…',
  sysup_title: '系统更新中',
  sysup_body: '通常约需一分钟，页面会自动重新连接，无需刷新。',
  sysup_lost_title: '连接中断',
  sysup_lost_body: '正在自动重新连接…',
  sysup_elapsed: '已用时 {{time}}',
  sysup_slow: '比平时久一些，仍在尝试连接。',
  sysup_retry: '立即重试',
  sysup_reload: '重新加载',
  sysup_continue: '仍然继续',
  sysup_back: '已恢复连接',
  sysup_finishing: '系统已恢复，正在完成更新…',
  sysup_dismiss: '关闭',
  sysup_progress_label: '更新进度',
  sysup_try_again: '系统正在更新或暂时无法连接，请一分钟后再试。',
};

export const th: Record<string, string> = {
  bgr_modal_title: 'ยืนยันการแก้ไขนี้',
  bgr_modal_lead: 'การยืนยันหมายความว่าการแก้ไขนี้ใช้งานได้ในระบบทดสอบแล้ว จะให้อัปเดตไปยังระบบอื่นเมื่อไร',
  bgr_confirm_only: 'ยืนยันว่าการแก้ไขนี้ใช้งานได้หรือไม่',
  bgr_options_unavailable: 'โหลดตัวเลือกการอัปเดตไม่ได้ หากยืนยันตอนนี้ การแก้ไขจะถูกอัปเดตโดยอัตโนมัติพร้อมรอบกลางคืน',
  bgr_opt_tonight: 'อัปเดตอัตโนมัติหลัง 20:00 คืนนี้',
  bgr_opt_tonight_hint: 'แนะนำ — ไม่รบกวนผู้ใช้ระหว่างเวลาทำงาน',
  bgr_opt_tonight_night: 'อัปเดตอัตโนมัติภายในไม่กี่นาทีข้างหน้า',
  bgr_opt_tonight_night_hint: 'ขณะนี้อยู่ในช่วงอัปเดตรอบกลางคืน ระบบจะอัปเดตทีละระบบ',
  bgr_opt_now: 'อัปเดตทุกระบบทันที',
  bgr_opt_now_hint: '{{systems}} จะได้รับการอัปเดตทีละระบบ แต่ละระบบจะรีสตาร์ตและใช้งานไม่ได้ประมาณหนึ่งนาที ผู้ที่กำลังใช้งานจะเห็นข้อความแจ้งว่ากำลังอัปเดต',
  bgr_systems_unknown: 'ระบบอื่น ๆ',
  bgr_systems_unknown_mid: 'ระบบอื่น ๆ',
  bgr_offline_warn: 'เครื่องปล่อยอัปเดตของ Bugify ติดต่อมาครั้งล่าสุดเมื่อ {{min}} นาทีที่แล้ว การอัปเดตจะเริ่มเมื่อเครื่องกลับมา หากนานเกิน 30 นาที การแก้ไขจะถูกอัปเดตพร้อมรอบกลางคืนแทน',
  bgr_busy_warn: 'กำลังมีการอัปเดตระบบเหล่านี้อยู่ รอบนี้จะเริ่มทันทีหลังจากรอบนั้นเสร็จ',
  bgr_not_before: 'จะเริ่มเวลา {{time}} เนื่องจากระบบเพิ่งรีสตาร์ตไป การอัปเดตทันทีแต่ละครั้งต้องห่างกันอย่างน้อย 30 นาที',
  bgr_failed_recent_warn: 'การอัปเดตครั้งก่อนหยุดที่ {{system}} เมื่อ {{min}} นาทีที่แล้ว การอัปเดตตอนนี้จะลองอีกครั้ง',
  bgr_fresh_warn: 'ระบบทดสอบเพิ่งอัปเดตเมื่อ {{min}} นาทีที่แล้ว ทุกอย่างในเวอร์ชันนั้นจะถูกส่งออกไปพร้อมกับการแก้ไขนี้',
  bgr_riding: 'การแก้ไขที่ยังไม่ได้ยืนยันซึ่งจะถูกส่งออกไปด้วย: {{n}} รายการ',
  bgr_submitting: 'กำลังบันทึก…',
  bgr_err_unauthenticated: 'เซสชันหมดอายุแล้ว กรุณาเข้าสู่ระบบอีกครั้ง',
  bgr_err_already: 'รายการนี้ได้รับการยืนยันแล้วโดย {{who}}',
  bgr_err_not_ai_done: 'รายการนี้ไม่ได้รอการยืนยันแล้ว',
  bgr_err_unknown_release: 'ไม่รู้จักตัวเลือกการอัปเดต กรุณาโหลดหน้าใหม่',
  bgr_err_bad_mode: 'ไม่รู้จักคำขออัปเดต กรุณาโหลดหน้าใหม่',
  bgr_err_now_unavailable: 'ขณะนี้ระบบนี้ยังอัปเดตทันทีไม่ได้',
  bgr_err_nothing_owed: 'ไม่มีรายการที่รอการอัปเดต',
  bgr_err_already_requested: 'มีการขออัปเดตทันทีไว้แล้ว',
  bgr_err_retry_used: 'การอัปเดตที่ล้มเหลวนี้ถูกลองใหม่ไปแล้วหนึ่งครั้ง ผู้ดูแลระบบสามารถลองอีกครั้งได้',
  bgr_err_retry_pointless: 'การอัปเดตครั้งก่อนถูกปฏิเสธโดยระบบที่ใช้โค้ดคนละสาย การลองใหม่จะไม่ช่วยจนกว่าจะรวมโค้ดนั้น',
  bgr_err_daily_cap: 'วันนี้ใช้สิทธิ์อัปเดตทันทีครบแล้ว การแก้ไขจะถูกอัปเดตพร้อมรอบกลางคืน',
  bgr_err_not_running: 'การอัปเดตนั้นไม่ได้ทำงานอยู่แล้ว',
  bgr_err_forbidden: 'เฉพาะผู้ดูแลระบบหรือผู้อนุมัติ Bugify เท่านั้นที่ทำได้',
  bgr_err_generic: 'บันทึกไม่สำเร็จ กรุณาลองใหม่',
  bgr_waiting: 'กำลังรอ Bugify เริ่มการอัปเดต…',
  bgr_waiting_hint: 'ปกติจะเริ่มภายในหนึ่งนาที',
  bgr_held: 'กำลังรอ: {{reason}} — Bugify จะลองใหม่ในอีกสักครู่',
  bgr_held_night: 'หยุดชั่วคราว: {{reason}} — Bugify จะลองใหม่ในอีกไม่กี่นาที',
  bgr_nightly: 'การอัปเดตรอบกลางคืน',
  bgr_preparing: 'กำลังเตรียมการอัปเดต…',
  bgr_between: 'กำลังไปยังระบบถัดไป…',
  bgr_finishing: 'กำลังเสร็จสิ้น…',
  bgr_running: 'กำลังอัปเดต {{system}} — {{step}}',
  bgr_of: 'ระบบที่ {{i}} จาก {{n}}',
  bgr_elapsed: 'ผ่านไป {{time}}',
  bgr_requested_by: 'ขอโดย {{who}}',
  bgr_stalled: 'ไม่ได้รับข่าวจากเครื่องปล่อยอัปเดตมา {{min}} นาที — อาจยังทำงานอยู่',
  bgr_slow: 'ใช้เวลานานกว่าปกติมาก — Bugify ยังรออยู่',
  bgr_done: 'อัปเดตทุกระบบเรียบร้อยแล้ว ใช้เวลา {{time}}',
  bgr_done_partial: 'อัปเดตแล้ว: {{names}} ระบบที่เหลือจะอัปเดตในอีกไม่กี่นาที',
  bgr_done_noop: 'ระบบอื่นทั้งหมดเป็นเวอร์ชันล่าสุดอยู่แล้ว',
  bgr_failed: 'การอัปเดตหยุดที่ {{system}}: {{error}}',
  bgr_failed_early: 'การอัปเดตหยุดก่อนที่จะแตะระบบใด ๆ: {{error}}',
  bgr_failed_partial: '{{system}} อาจอัปเดตไม่ครบ — กรุณาตรวจสอบระบบนี้ก่อน',
  bgr_failed_hint: 'ระบบถัดไปไม่ได้ถูกแตะต้อง Bugify จะไม่ลองใหม่เองภายใน 6 ชั่วโมง เมื่อแก้ปัญหาแล้วให้กด "ลองใหม่ทันที"',
  bgr_blocked: 'เริ่มการอัปเดตไม่ได้: {{reason}} ไม่มีการอัปเดตใด ๆ',
  bgr_blocked_hint: 'เมื่อแก้ปัญหาแล้ว การแก้ไขจะถูกอัปเดตพร้อมรอบกลางคืน หรือกด "ลองใหม่ทันที"',
  bgr_failed_hint_noretry: 'ระบบถัดไปไม่ได้ถูกแตะต้อง Bugify จะไม่ลองใหม่เองภายใน 6 ชั่วโมง',
  bgr_blocked_hint_noretry: 'เมื่อแก้ปัญหาแล้ว การแก้ไขจะถูกอัปเดตพร้อมรอบกลางคืน',
  bgr_now_unavail_followers: 'ยังอัปเดตทันทีไม่ได้: เครื่องปล่อยอัปเดตของ Bugify ไม่ได้ตั้งค่าระบบอื่นไว้',
  bgr_now_unavail_offline: 'ยังอัปเดตทันทีไม่ได้: เครื่องปล่อยอัปเดตของ Bugify ติดต่อมาครั้งล่าสุดเมื่อ {{min}} นาทีที่แล้ว เมื่อเครื่องกลับมา การแก้ไขจะถูกอัปเดตพร้อมรอบกลางคืน',
  bgr_now_unavail_never: 'ยังอัปเดตทันทีไม่ได้: เครื่องปล่อยอัปเดตของ Bugify ยังไม่เคยติดต่อเข้ามา',
  bgr_checked: 'ตรวจสอบเท่านั้น — ขั้นตอนการอัปเดตใช้งานได้ และไม่มีการอัปเดตใด ๆ',
  bgr_stopped: '{{who}} หยุดการอัปเดตแล้ว อัปเดตก่อนหยุด: {{names}} ระบบที่เหลือจะอัปเดตพร้อมรอบกลางคืน',
  bgr_none: 'ไม่มี',
  bgr_expired: 'การอัปเดตไม่ได้เริ่มภายใน 30 นาที ({{reason}}) จะอัปเดตอัตโนมัติหลัง 20:00 คืนนี้',
  bgr_expired_night: 'การอัปเดตไม่ได้เริ่มภายใน 30 นาที ({{reason}}) จะอัปเดตพร้อมรอบกลางคืนในอีกไม่กี่นาที',
  bgr_lost: 'ขาดการติดต่อกับเครื่องปล่อยอัปเดตระหว่างการอัปเดต ระบบอาจยังอัปเดตไม่เสร็จ จะลองใหม่หลัง 20:00 คืนนี้',
  bgr_lost_night: 'ขาดการติดต่อกับเครื่องปล่อยอัปเดตระหว่างการอัปเดต ระบบอาจยังอัปเดตไม่เสร็จ รอบกลางคืนจะลองใหม่ในอีกไม่กี่นาที',
  bgr_deploy_now_btn: 'อัปเดตทันที',
  bgr_retry_btn: 'ลองใหม่ทันที',
  bgr_now_confirm: 'อัปเดต {{systems}} ตอนนี้เลยหรือไม่ แต่ละระบบจะรีสตาร์ตและใช้งานไม่ได้ประมาณหนึ่งนาที',
  bgr_stop_btn: 'หยุดหลังระบบนี้',
  bgr_stop_confirm: 'หยุดการอัปเดตหลัง {{system}} หรือไม่ ระบบที่อัปเดตแล้วจะคงเดิม ระบบที่เหลือจะอัปเดตพร้อมรอบกลางคืน',
  bgr_stopping: 'จะหยุดหลัง {{system}}…',
  bgr_fanout_now: 'กำลังอัปเดตไปยังระบบอื่นตอนนี้ — ดูความคืบหน้าด้านล่าง',
  bgr_progress_label: 'ความคืบหน้าการอัปเดต',
  bgr_t_pending: 'รอคิว',
  bgr_t_not_updated: 'ไม่ได้อัปเดต',
  bgr_t_current: 'เป็นเวอร์ชันล่าสุดแล้ว',
  bgr_t_done: 'อัปเดตแล้ว',
  bgr_t_failed: 'ล้มเหลว',
  bgr_t_held: 'พักไว้',
  bgr_step_start: 'กำลังเริ่ม',
  bgr_step_lock: 'รอเซิร์ฟเวอร์',
  bgr_step_gates: 'ตรวจสอบความปลอดภัย',
  bgr_step_build: 'สร้างเวอร์ชันใหม่',
  bgr_step_tsc: 'ตรวจสอบโค้ดฝั่งเซิร์ฟเวอร์',
  bgr_step_upload: 'กำลังอัปโหลด',
  bgr_step_restart: 'กำลังรีสตาร์ต',
  bgr_step_verify: 'รอให้ระบบตอบสนอง',
  bgr_step_health: 'ตรวจสอบว่าระบบกลับมาแล้ว',
  bgr_banner_running: 'กำลังอัปเดตระบบอื่น — {{system}}: {{step}}',
  bgr_banner_waiting: 'ขออัปเดตทันทีแล้ว — กำลังรอ Bugify เริ่ม',
  bgr_banner_done: 'อัปเดตเสร็จแล้ว: {{names}}',
  bgr_banner_failed: 'การอัปเดตหยุดที่ {{system}}',
  bgr_banner_failed_early: 'การอัปเดตหยุดก่อนแตะระบบใด ๆ',
  bgr_banner_blocked: 'การอัปเดตทันทีไม่ได้เริ่ม: {{reason}}',
  bgr_banner_open: 'รายละเอียด',
  bgr_why_lock_busy: 'มีการปล่อยอัปเดตไปยังระบบเหล่านี้อยู่แล้ว',
  bgr_why_server_lock: 'มีการอัปเดตอื่นกำลังใช้เซิร์ฟเวอร์ของระบบนั้นอยู่',
  bgr_why_source_unknown: 'อ่านเวอร์ชันปัจจุบันของระบบทดสอบไม่ได้',
  bgr_why_source_dirty: 'ระบบทดสอบกำลังรันการเปลี่ยนแปลงที่ยังไม่ได้ commit — กรุณา commit และ deploy ใหม่ก่อน',
  bgr_why_source_down: 'ระบบทดสอบไม่ตอบสนอง',
  bgr_why_source_moved: 'ระบบทดสอบถูกอัปเดตระหว่างที่กำลังเตรียมการอัปเดต',
  bgr_why_soak: 'ระบบทดสอบเพิ่งอัปเดตด้วยการเปลี่ยนแปลงที่ยังไม่มีใครตรวจ จึงต้องรอให้เวอร์ชันนั้นทำงานสักระยะ',
  bgr_why_commit_not_live: 'ระบบทดสอบยังไม่ได้รันการแก้ไขที่ยืนยันแล้ว',
  bgr_why_check_only: 'ตรวจสอบเท่านั้น — ไม่มีการอัปเดตใด ๆ',
  bgr_why_no_followers: 'ไม่ได้ตั้งค่าระบบอื่นไว้',
  bgr_why_config: 'การตั้งค่าของระบบหนึ่งบนเครื่องปล่อยอัปเดตหายไปหรือไม่ครบ',
  bgr_why_commit_missing: 'เครื่องปล่อยอัปเดตไม่มีเวอร์ชันที่ระบบทดสอบกำลังรันอยู่',
  bgr_why_worktree: 'เครื่องปล่อยอัปเดตเตรียมสำเนาโค้ดไม่ได้',
  bgr_why_deploy_failed: 'การอัปเดตระบบนั้นล้มเหลว',
  bgr_why_no_answer: 'ระบบไม่ตอบสนองหลังรีสตาร์ต',
  bgr_why_marker_stuck: 'ระบบไม่ได้บันทึกเวอร์ชันใหม่',
  bgr_why_diverged: 'ระบบนั้นใช้โค้ดคนละสาย จึงไม่ได้แตะต้องระบบนั้น',
  bgr_why_interrupted: 'การปล่อยอัปเดตถูกขัดจังหวะระหว่างที่กำลังอัปเดต',
  bgr_why_overtime: 'การปล่อยอัปเดตทำงานมานานกว่า 3 ชั่วโมง',
  bgr_why_no_summary: 'สคริปต์ปล่อยอัปเดตจบโดยไม่มีรายงาน',
  bgr_why_stopped: 'หยุดตามคำขอ',
  bgr_why_not_in_tree: 'เครื่องปล่อยอัปเดตยังไม่มีสคริปต์ปล่อยอัปเดต',
  bgr_why_unaccounted: 'การปล่อยอัปเดตรายงานว่าสำเร็จแต่ไม่ได้ระบุระบบใดเลย',
  bgr_why_not_picked: 'เครื่องปล่อยอัปเดตไม่ได้รับงานนี้',
  bgr_why_recent_failure: 'การปล่อยอัปเดตประจำคืนของเวอร์ชันนี้เพิ่งล้มเหลว จึงจะไม่ลองใหม่จนกว่าจะมีคนตรวจสอบ',
  sysup_banner: 'ระบบกำลังอัปเดต อีกสักครู่จะใช้งานไม่ได้ประมาณหนึ่งนาที กรุณาบันทึกงานของคุณ',
  sysup_reconnecting: 'กำลังเชื่อมต่อเซิร์ฟเวอร์ใหม่…',
  sysup_title: 'กำลังอัปเดตระบบ',
  sysup_body: 'โดยปกติใช้เวลาประมาณหนึ่งนาที หน้านี้จะเชื่อมต่อใหม่โดยอัตโนมัติ ไม่ต้องรีเฟรช',
  sysup_lost_title: 'การเชื่อมต่อขาดหาย',
  sysup_lost_body: 'กำลังพยายามเชื่อมต่อใหม่โดยอัตโนมัติ…',
  sysup_elapsed: 'ผ่านไป {{time}}',
  sysup_slow: 'ใช้เวลานานกว่าปกติ ยังคงพยายามเชื่อมต่ออยู่',
  sysup_retry: 'ลองอีกครั้งตอนนี้',
  sysup_reload: 'โหลดใหม่',
  sysup_continue: 'ดำเนินการต่อ',
  sysup_back: 'กลับมาออนไลน์แล้ว',
  sysup_finishing: 'ระบบกลับมาแล้ว กำลังทำการอัปเดตให้เสร็จ…',
  sysup_dismiss: 'ปิด',
  sysup_progress_label: 'ความคืบหน้าการอัปเดต',
  sysup_try_again: 'ระบบกำลังอัปเดตหรือเชื่อมต่อไม่ได้ กรุณาลองใหม่ในอีกหนึ่งนาที',
};
